import { BasketCategory, BasketItem } from "../features/orders/itemTypes";
import {
  InventoryItem,
  InventoryCleaningType,
} from "../features/inventory/types";
import { PromoCode } from "../features/promo/types";

export const parsePrice = (price: string) => {
  const numericValue = Number.parseFloat(price.replace(/[^0-9.]/g, ""));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const normalizeBaseUrl = (value: string) => value.replace(/\/+$/, "");

export const resolveInventoryImageUrl = (image: string) => {
  const trimmedImage = image.trim();

  if (!trimmedImage) {
    return "";
  }

  if (/^(https?:|data:|blob:)/i.test(trimmedImage)) {
    return trimmedImage;
  }

  const apiUrl = process.env.REACT_APP_API_URL?.trim() ?? "";
  const explicitImageBaseUrl =
    process.env.REACT_APP_IMAGE_BASE_URL?.trim() ?? "";
  const fallbackOrigin =
    typeof window !== "undefined" ? window.location.origin : "";
  const baseUrl = normalizeBaseUrl(
    explicitImageBaseUrl || apiUrl || fallbackOrigin,
  );

  if (!baseUrl) {
    return trimmedImage;
  }

  if (trimmedImage.startsWith("/")) {
    return `${baseUrl}${trimmedImage}`;
  }

  if (trimmedImage.includes("/")) {
    return `${baseUrl}/${trimmedImage}`;
  }

  return `${baseUrl}/${trimmedImage}`;
};

export const parseCleaningTypes = (value: string): InventoryCleaningType[] => {
  if (!value.trim()) {
    return [];
  }

  try {
    const normalized = value.replace(/'/g, '"');
    const parsed = JSON.parse(normalized) as Array<{
      title?: string;
      price?: string;
    }>;

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((entry) => entry.title && entry.price)
      .map((entry) => ({
        title: entry.title as string,
        price: entry.price as string,
      }));
  } catch {
    return [];
  }
};

export const mapInventoriesToBasketItems = (
  inventories: InventoryItem[],
  previousItems: BasketItem[] = [],
): BasketItem[] => {
  const previousItemMap = new Map(previousItems.map((item) => [item.id, item]));

  return inventories
    .filter((inventory) => inventory.is_active)
    .map((inventory) => {
      const previousItem = previousItemMap.get(inventory.id);
      const previousServiceMap = new Map(
        previousItem?.services.map((service) => [service.id, service.qty]) ??
          [],
      );

      const services =
        inventory.cleaningTypes.length > 0
          ? inventory.cleaningTypes.map((service, index) => {
              const serviceId = `${inventory.id}-${index}`;
              return {
                id: serviceId,
                title: service.title,
                price: `QAR ${service.price}`,
                qty: previousServiceMap.get(serviceId) ?? 0,
              };
            })
          : [
              {
                id: `${inventory.id}-default`,
                title: "Standard",
                price: `QAR ${inventory.price}`,
                qty: previousServiceMap.get(`${inventory.id}-default`) ?? 0,
              },
            ];

      return {
        id: inventory.id,
        title: inventory.name,
        image: inventory.image,
        category: "Traditional" as BasketCategory,
        services,
      };
    });
};

export const formatPromoDiscount = (promoCode?: PromoCode | null) => {
  if (!promoCode) {
    return "";
  }

  const discountValue = Number.parseFloat(promoCode.discount);

  if (!Number.isFinite(discountValue)) {
    return `${promoCode.code} applied`;
  }

  if (promoCode.discount_type === "round") {
    return `${promoCode.code} applied - QAR ${discountValue} off`;
  }

  return `${promoCode.code} applied - ${discountValue}% off`;
};
